import { format } from 'date-fns';
import classNames from 'classnames';

import Message from 'types/Message';
import { Conference } from 'types/models/Conference';
import SectionHeader from '@components/SectionHeader';
import Typography from '@components/atoms/Typography';
import Button from '@components/atoms/Button';
import Link from '@components/atoms/Link';
import { FormattedMessage } from 'utils/intl';
import getFullStreetAddress from 'utils/getFullStreetAddress';
import { scrollToAnchor } from 'utils/scroll';
import compact from 'utils/compact';

import classes from './ConferenceAdditionalInfo.module.scss';
import messages from './ConferenceAdditionalInfo.messages';

type InfoProps = {
  label: Message;
  value: Message;
};

const Info = ({ label, value }: InfoProps) => (
  <div className={classes.info}>
    <Typography variant="h4" weight="700" renderAs="span">
      {label}:
    </Typography>
    <Typography variant="h4" renderAs="span">
      {value}
    </Typography>
  </div>
);

type Props = {
  conference: Conference;
  className?: string;
};

const ConferenceAdditionalInfo = ({ className, conference }: Props) => {
  const address = conference.location?.address;
  const location = compact([
    address.companyName,
    getFullStreetAddress(address),
    `${address.postalCode} ${address.locality}`.trim(),
  ]).join(', ');
  const mapUrl =
    conference.type.id === 'STATIONARY' ? `https://maps.google.pl/maps?${new URLSearchParams({ q: location })}` : '';

  return (
    <div className={classNames(classes.wrapper, className)}>
      <SectionHeader renderAs="div">
        <FormattedMessage {...messages.header} />
      </SectionHeader>
      <div className={classes.items}>
        <Info label={<FormattedMessage {...messages.type} />} value={conference.type.name} />
        <Info
          label={<FormattedMessage {...messages.start} />}
          value={format(new Date(conference.dateFrom), 'd.MM.yyyy - HH:mm')}
        />
        <Info
          label={<FormattedMessage {...messages.end} />}
          value={format(new Date(conference.dateTo), 'd.MM.yyyy - HH:mm')}
        />
        {address && conference.type.id === 'STATIONARY' && (
          <Info
            label={<FormattedMessage {...messages.localization} />}
            value={
              <div className={classes.location}>
                {location}
                <Link className={classes.link} href={mapUrl} openInNewTab>
                  <FormattedMessage {...messages.seeOnMap} />
                </Link>
              </div>
            }
          />
        )}
      </div>
      <Button size="lg" color="tertiary" disabled={conference.isLocked} onClick={() => scrollToAnchor('register')} wide>
        <FormattedMessage {...messages.register} />
      </Button>
    </div>
  );
};

export default ConferenceAdditionalInfo;
